import { GoogleMap, useJsApiLoader } from '@react-google-maps/api'
import React, { useEffect, useRef } from 'react'
import { MapPinsBlack } from './map-pins-black'
import { MapPinsWhite } from './map-pins-white'

interface Location {
	lat: number
	lng: number
	storeName: string
	storeId: string
	storeChannelKey: string
}

interface GoogleMapComponentProps {
	locations: Location[]
	zoom: number
	selectedStoreChannelKey?: string
	onMarkerClick: (storeChannelKey: string) => void
	center: google.maps.LatLngLiteral
	height?: string
}

const libraries: (
	| 'places'
	| 'drawing'
	| 'geometry'
	| 'visualization'
	| 'marker'
)[] = ['places', 'marker']

const getIconKey = (storeId: string, iconSet: Record<string, string>) => {
	for (const key in iconSet) {
		if (storeId.includes(key)) {
			return key
		}
	}
	return 'default'
}

const GoogleMapComponent: React.FC<GoogleMapComponentProps> = ({
	locations,
	zoom,
	selectedStoreChannelKey,
	onMarkerClick,
	center,
	height,
}) => {
	const containerStyle = {
		width: '100%',
		height: height ? height : '250px',
	}
	const mapRef = useRef<google.maps.Map | null>(null)
	const markerMapRef = useRef<
		Map<string, google.maps.marker.AdvancedMarkerElement>
	>(new Map())

	const { isLoaded } = useJsApiLoader({
		googleMapsApiKey: ENV.GOOGLE_MAPS_API_KEY as string,
		libraries: libraries,
	})

	useEffect(() => {
		if (selectedStoreChannelKey && mapRef.current) {
			const selectedLocation = locations.find(
				location => location.storeChannelKey === selectedStoreChannelKey,
			)
			if (selectedLocation) {
				mapRef.current.panTo({
					lat: selectedLocation.lat,
					lng: selectedLocation.lng,
				})
				mapRef.current.setZoom(15)
				updateMarkerIcons(selectedStoreChannelKey)
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedStoreChannelKey, locations])

	useEffect(() => {
		if (mapRef.current) {
			mapRef.current.panTo(center)
			mapRef.current.setZoom(zoom)
		}
	}, [center, zoom])

	const addMarkers = async (map: google.maps.Map) => {
		const { AdvancedMarkerElement } = (await (google.maps as any).importLibrary(
			'marker',
		)) as {
			AdvancedMarkerElement: typeof google.maps.marker.AdvancedMarkerElement
		}

		locations.forEach(location => {
			const iconKey = getIconKey(location.storeId, MapPinsWhite)

			const marker = new AdvancedMarkerElement({
				map,
				position: { lat: location.lat, lng: location.lng },
				title: location.storeName,
				content: createMarkerIcon(
					`data:image/svg+xml;base64,${btoa(MapPinsWhite[iconKey])}`,
				),
				gmpClickable: true,
			})

			marker.addListener('click', () => {
				if (marker.position) {
					map.panTo(
						marker.position as google.maps.LatLng | google.maps.LatLngLiteral,
					)
					map.setZoom(15)
					onMarkerClick(location.storeChannelKey)
					updateMarkerIcons(location.storeChannelKey)
				}
			})

			markerMapRef.current.set(location.storeChannelKey, marker)
		})

		if (selectedStoreChannelKey) {
			updateMarkerIcons(selectedStoreChannelKey)
		}
	}

	const updateMarkerIcons = (selectedChannelKey: string) => {
		markerMapRef.current.forEach((marker, storeChannelKey) => {
			const location = locations.find(
				loc => loc.storeChannelKey === storeChannelKey,
			)
			if (location) {
				const iconSet =
					storeChannelKey === selectedChannelKey ? MapPinsBlack : MapPinsWhite
				const iconKey = getIconKey(location.storeId, iconSet)
				marker.content = createMarkerIcon(
					`data:image/svg+xml;base64,${btoa(iconSet[iconKey])}`,
				)
			}
		})
	}

	const createMarkerIcon = (iconUrl: string) => {
		const iconContainer = document.createElement('div')
		iconContainer.style.width = '80px'
		iconContainer.style.height = '80px'
		iconContainer.style.backgroundImage = `url(${iconUrl})`
		iconContainer.style.backgroundSize = 'contain'
		iconContainer.style.backgroundRepeat = 'no-repeat'
		iconContainer.style.backgroundPosition = 'center'
		iconContainer.style.cursor = 'pointer'
		return iconContainer
	}

	const mapOptions = {
		disableDefaultUI: true,
		zoomControl: true,
		cursor: 'pointer',
		mapId: 'DEMO_MAP_ID',
	}

	if (!isLoaded) {
		return <div>Loading...</div>
	}
	return (
		<GoogleMap
			mapContainerStyle={containerStyle}
			center={center}
			zoom={zoom}
			onLoad={mapInstance => {
				mapRef.current = mapInstance
				addMarkers(mapInstance)
			}}
			options={mapOptions}
		/>
	)
}

export default GoogleMapComponent
